import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { LocationSuggestion } from '~/services/api/community/types';

import { LabeledTextInput } from './ui/LabeledTextInput';
import { type LocationChange, LocationPicker } from './ui/LocationPicker';
import { PillButton } from './ui/PillButton';

import type useBuiltInProfileFields from '../_hooks/useBuiltInProfileFields';
import { BUILT_IN_FIELD_MAX_LENGTH } from '../_hooks/useBuiltInProfileFields';
import { submitLabel } from '../_utils/submitLabel';
import { OnboardHeader } from '../onboard/_components/OnboardHeader';
import { ProfileAvatarPicker } from './ProfileAvatarPicker';

type RequiredBuiltInFieldsPageProps = {
  builtIn: ReturnType<typeof useBuiltInProfileFields>;
  needsAvatar: boolean;
  needsHeadline: boolean;
  needsLocation: boolean;
  firstName: string;
  totalSteps: number;
  disabled: boolean;
  isLastPage: boolean;
  locationSuggestion?: LocationSuggestion | null;
  avatarUrl?: string;
  isDefaultAvatar?: boolean;
  avatarError?: string;
  onAvatarSelect: (file: File) => void;
  onLocationChange: (change: LocationChange) => void;
};

/** The built-in half of required profile setup; the custom questions follow on their own pages. */
export const RequiredBuiltInFieldsPage: FC<RequiredBuiltInFieldsPageProps> = ({
  builtIn,
  needsAvatar,
  needsHeadline,
  needsLocation,
  firstName,
  totalSteps,
  disabled,
  isLastPage,
  locationSuggestion,
  avatarUrl,
  isDefaultAvatar,
  avatarError,
  onAvatarSelect,
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const welcomeName = firstName.trim();

  return (
    <>
      <OnboardHeader
        title={
          welcomeName
            ? t('community.profile_setup.welcome_title', { name: welcomeName })
            : t('community.profile_setup.title')
        }
        subtitle={t('community.profile_setup.subtitle', {
          count: Number(needsAvatar) + Number(needsHeadline) + Number(needsLocation),
        })}
        currentStep={0}
        totalSteps={totalSteps}
      />
      {needsAvatar && (
        <div className="flex flex-col gap-2">
          <ProfileAvatarPicker
            currentImageUrl={avatarUrl}
            isDefaultAvatar={isDefaultAvatar}
            onFileSelect={onAvatarSelect}
            disabled={disabled}
          />
          {avatarError && (
            <p className="text-center text-[13px] leading-4 text-red-600" role="alert">
              {avatarError}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-col gap-4">
        {needsHeadline && (
          <LabeledTextInput
            id="required-setup-headline"
            name="headline"
            label={builtIn.headlineField.display ?? t('community.onboard.headline_label')}
            placeholder={t('community.onboard.headline_placeholder')}
            maxLength={BUILT_IN_FIELD_MAX_LENGTH}
            value={builtIn.headline}
            required
            error={builtIn.errors.headline}
            disabled={disabled}
            autoFocus
            onChange={(event) => builtIn.setHeadline(event.target.value)}
          />
        )}
        {needsLocation && (
          <LocationPicker
            id="required-setup-location"
            label={builtIn.locationField.display ?? t('community.onboard.location_label')}
            placeholder={t('community.onboard.location_placeholder')}
            maxLength={BUILT_IN_FIELD_MAX_LENGTH}
            value={builtIn.location}
            required
            error={builtIn.errors.location}
            disabled={disabled}
            suggestion={locationSuggestion}
            onChange={onLocationChange}
          />
        )}
      </div>
      <PillButton variant="primary" type="submit" size="lg" fullWidth disabled={disabled}>
        {submitLabel(t, disabled, isLastPage)}
      </PillButton>
    </>
  );
};
